// Leadership Sustainability V1 — pure computation, no Firebase. Takes
// already-loaded docs (a unit, its current-state unitLeaders, its SYT
// records, and its unitLeaderSnapshots history) and produces the FACTS a
// screen can render. Same style/precedent as evaluateHazardousWeather() /
// isKeyThreeRole() in this same directory (src/lib/unitHealth.ts) — a
// canonical, side-effect-free evaluator, tested directly (see
// scripts/test-leadership-sustainability.ts), never re-derived ad hoc in a
// component.
//
// PRODUCT PRINCIPLE (read this before changing anything below): Compass
// answers "what do we know about this unit's leadership structure, and
// what deserves a closer look" — never "this unit is healthy/unhealthy."
// There is deliberately no overall numeric score anywhere in this file.
// Every AttentionSignal must be explainable from the facts already
// computed alongside it (see ATTENTION SIGNAL DEFINITIONS below) — no
// hidden weighting, no color grade that isn't reproducible by reading this
// file. See docs/product-module-architecture.md's Leadership Sustainability
// section for the full design rationale and the discovery this followed.
//
// COMMISSIONER TOOLS BOUNDARY: nothing in this file reads Unit Commissioner
// assignment, commissioner contact/assessment data, or any Commissioner
// Tools report. See `commissioner` fields on UnitLeadershipSustainability
// below — always null in V1, reserved so a future Commissioner enrichment
// can populate them without a data-model change here.
//
// AUTHORIZATION BOUNDARY: this file NEVER reads tenantMemberships, grants,
// or revokes anything. It only ever reads unitLeaders/unitSyt/key3/
// unitLeaderSnapshots — data ABOUT leadership, never data that CONTROLS
// Compass access. See scripts/test-leadership-sustainability.ts's
// boundary checks.

import type {
  ISODateString,
  Unit,
  UnitKey3Person,
  UnitLeader,
  UnitLeaderSnapshot,
  UnitSyt,
  UnitType,
} from '../types';

// ---- Data availability -----------------------------------------------
// Every section distinguishes "we have no trustworthy data" from "we have
// data and it says zero/none" — the former must never render as the
// latter. See the file-level product principle above.

export type Availability = 'available' | 'insufficient';

// ---- Position-role classification --------------------------------------
// Real position strings on file (verified against production Trained
// Leaders data — see src/lib/unitHealth.ts's own header comment for the
// same verification approach): "Cubmaster", "Scoutmaster", "Assistant
// Scoutmaster", "Den Leader", "Asst. Den Leader" / "Assistant Den Leader",
// "Committee Chair" / "Pack Committee Chair", "Chartered Organization
// Representative", "Advisor" / "Crew Advisor", "Skipper", "Coach". BSA
// reports abbreviate "Assistant" inconsistently ("Assistant X" vs "Asst.
// X"), so the assistant-prefix check below handles both forms rather than
// assuming one spelling.

function norm(position: string): string {
  return position.trim().toLowerCase();
}

function isAssistantPrefixed(normalized: string): boolean {
  return /^(assistant|associate|asst\.?)\s+/.test(normalized);
}

function stripAssistantPrefix(normalized: string): string {
  return normalized.replace(/^(assistant|associate|asst\.?)\s+/, '');
}

/** The unit's own top program leader position — Cubmaster/Scoutmaster for
 *  the two unit types V1 has confident mappings for; a generic "unit
 *  leader" match (same regex the Key 3 parser itself already uses, see
 *  unitReports.ts's LEADER_POSITIONS) for every other type, since V1
 *  doesn't have a confidently-verified title for Crew/Ship/Post/Club. */
export function isTopUnitLeaderRole(position: string, unitType: UnitType): boolean {
  const p = norm(position);
  if (isAssistantPrefixed(p)) return false;
  if (unitType === 'pack') return p === 'cubmaster';
  if (unitType === 'troop') return p === 'scoutmaster';
  return /^(cubmaster|scoutmaster|advisor|crew advisor|skipper|coach)$/.test(p);
}

/** The assistant/associate variant of the top leader role, for the two
 *  unit types V1 maps confidently. Not computed for other unit types —
 *  see the file's unit-type-awareness note near UNIT_TYPE_PROGRAM_ROLES. */
export function isAssistantTopLeaderRole(position: string, unitType: UnitType): boolean {
  const p = norm(position);
  if (!isAssistantPrefixed(p)) return false;
  const rest = stripAssistantPrefix(p);
  if (unitType === 'pack') return rest === 'cubmaster';
  if (unitType === 'troop') return rest === 'scoutmaster';
  return false;
}

export function isDenLeaderRole(position: string): boolean {
  const p = norm(position);
  return !isAssistantPrefixed(p) && /(^|\s)den leader$/.test(p);
}

export function isAssistantDenLeaderRole(position: string): boolean {
  const p = norm(position);
  return isAssistantPrefixed(p) && /den leader$/.test(stripAssistantPrefix(p));
}

/** The unique-people set this file treats as "directly delivering the
 *  youth program" for a unit — top leader + assistant top leader, plus
 *  (pack only) Den Leader/Assistant Den Leader. See
 *  computeProgramFacingReadiness() below, and the V1.1 discovery this
 *  reuses the definition from (docs/product-module-architecture.md
 *  §17.13) — NOT a position-row count; a person holding two of these
 *  roles at once is still one person here, same as everywhere else in
 *  this file. Troops have no third/fourth program-facing role to add —
 *  see isAssistantTopLeaderRole's own note on why nothing is invented to
 *  make Pack/Troop symmetrical. */
function isProgramFacingRole(position: string, unitType: UnitType): boolean {
  return (
    isTopUnitLeaderRole(position, unitType) ||
    isAssistantTopLeaderRole(position, unitType) ||
    (unitType === 'pack' && (isDenLeaderRole(position) || isAssistantDenLeaderRole(position)))
  );
}

// A real title confirmed on file in production ("Pack Trainer") matches
// NONE of the classifiers above — deliberately. It is neither a top/
// assistant-top leader role nor a Den Leader role, and BSA's actual Pack
// Trainer function (delivering leader/YPT-adjacent training within the
// pack) is a training-support role, not a program-delivery or
// top-leader-succession role. It is correctly left unclassified here: it
// still counts toward Leadership Depth and the whole-roster Training
// fact, exactly like Committee Member/Charter Org Rep, just not toward
// Program Leadership or Program-Facing Readiness. See the V1.1 discovery
// (docs/product-module-architecture.md §17.13) for the investigation this
// documents — no normalization gap was found; this is intentional.

// ---- Key Leadership (current-state, from Unit.key3) --------------------
// Uses the ALREADY-CANONICAL hasCommitteeChair/hasUnitLeader/
// hasCharterOrgRep booleans computed by parseKey3Report() (unitReports.ts)
// as the authoritative "is this seat filled" answer — never re-derives
// that decision here. The matching regexes below are used ONLY to pick
// which key3.people[] entry to DISPLAY under each seat label; they
// intentionally mirror parseKey3Report()'s own patterns exactly
// (committee chair / LEADER_POSITIONS / chartered organization rep) so the
// displayed person can never disagree with the canonical boolean.

const KEY3_COMMITTEE_CHAIR_RE = /committee chair/i;
const KEY3_UNIT_LEADER_RE = /cubmaster|scoutmaster|advisor|skipper/i;
const KEY3_CHARTER_ORG_REP_RE = /chartered organization rep/i;

export function unitLeaderSeatLabel(unitType: UnitType): string {
  if (unitType === 'pack') return 'Cubmaster';
  if (unitType === 'troop') return 'Scoutmaster';
  return 'Unit Leader';
}

export interface KeySeatObservation {
  seatLabel: string;
  observed: boolean;
  personName: string | null;
  yptRaw: string | null;
}

export interface KeyLeadershipCoverage {
  availability: Availability;
  seats: KeySeatObservation[];
  asOf: ISODateString | null;
}

function findKey3Person(people: UnitKey3Person[], re: RegExp): UnitKey3Person | undefined {
  return people.find((p) => re.test(p.position));
}

export function computeKeyLeadership(unit: Unit): KeyLeadershipCoverage {
  if (!unit.key3) {
    return { availability: 'insufficient', seats: [], asOf: null };
  }
  const k3 = unit.key3;
  const seats: KeySeatObservation[] = [
    {
      seatLabel: unitLeaderSeatLabel(unit.type),
      observed: k3.hasUnitLeader,
      personName: findKey3Person(k3.people, KEY3_UNIT_LEADER_RE)?.name ?? null,
      yptRaw: findKey3Person(k3.people, KEY3_UNIT_LEADER_RE)?.yptRaw ?? null,
    },
    {
      seatLabel: 'Committee Chair',
      observed: k3.hasCommitteeChair,
      personName: findKey3Person(k3.people, KEY3_COMMITTEE_CHAIR_RE)?.name ?? null,
      yptRaw: findKey3Person(k3.people, KEY3_COMMITTEE_CHAIR_RE)?.yptRaw ?? null,
    },
    {
      seatLabel: 'Chartered Organization Representative',
      observed: k3.hasCharterOrgRep,
      personName: findKey3Person(k3.people, KEY3_CHARTER_ORG_REP_RE)?.name ?? null,
      yptRaw: findKey3Person(k3.people, KEY3_CHARTER_ORG_REP_RE)?.yptRaw ?? null,
    },
  ];
  return { availability: 'available', seats, asOf: k3.reportDate };
}

// ---- Leadership Depth (current-state, from unitLeaders) -----------------
// `leaders` MUST already be filtered by the caller to this unit's own
// scope==='unit' && unitId===unit.id records (see leadersByUnit in
// UnitHealth.tsx) — a record with an unresolved unitId is never counted
// against ANY unit here, by construction (it was never linked in the
// first place). See docs/product-module-architecture.md §17.11 for the
// 7-unresolved-designation data-quality background this depends on.

export interface LeadershipDepth {
  availability: Availability;
  count: number | null;
  asOf: ISODateString | null;
}

/** `trainedLeadersImportExists` distinguishes "Compass has never received
 *  a Trained Leaders report for this tenant" (insufficient — depth must
 *  never read as a false zero) from "Compass has current data and this
 *  unit genuinely has zero registered leaders right now" (a real, if
 *  concerning, zero). */
export function computeLeadershipDepth(
  leaders: UnitLeader[],
  trainedLeadersImportExists: boolean,
): LeadershipDepth {
  if (!trainedLeadersImportExists) {
    return { availability: 'insufficient', count: null, asOf: null };
  }
  return { availability: 'available', count: leaders.length, asOf: leaders[0]?.reportDate ?? null };
}

// ---- Program-facing leadership coverage ---------------------------------

export interface ProgramLeadershipCoverage {
  availability: Availability;
  unitType: UnitType;
  topLeaderLabel: string;
  topLeaderCount: number;
  assistantTopLeaderLabel: string | null; // null when not mapped for this unit type
  assistantTopLeaderCount: number | null;
  denLeaderCount: number | null; // pack only — null (not "0") means not applicable
  assistantDenLeaderCount: number | null;
  asOf: ISODateString | null;
  /** Precomputed compact one-line summary, e.g. "Cubmaster + 5 Den Leaders"
   *  or "Scoutmaster + 0 Assistant Scoutmasters" — matches the exact
   *  phrasing pattern from the product spec so the UI never re-derives
   *  wording. */
  summary: string;
}

export function computeProgramLeadership(
  unit: Unit,
  leaders: UnitLeader[],
  trainedLeadersImportExists: boolean,
): ProgramLeadershipCoverage {
  const unitType = unit.type;
  const topLabel = unitLeaderSeatLabel(unitType);
  if (!trainedLeadersImportExists) {
    return {
      availability: 'insufficient',
      unitType,
      topLeaderLabel: topLabel,
      topLeaderCount: 0,
      assistantTopLeaderLabel: null,
      assistantTopLeaderCount: null,
      denLeaderCount: null,
      assistantDenLeaderCount: null,
      asOf: null,
      summary: 'Insufficient Data',
    };
  }

  const asOf = leaders[0]?.reportDate ?? null;
  const topLeaderCount = leaders.filter((l) =>
    l.positions.some((p) => isTopUnitLeaderRole(p.position, unitType)),
  ).length;

  if (unitType === 'pack') {
    const denLeaderCount = leaders.filter((l) => l.positions.some((p) => isDenLeaderRole(p.position))).length;
    const assistantDenLeaderCount = leaders.filter((l) =>
      l.positions.some((p) => isAssistantDenLeaderRole(p.position)),
    ).length;
    const assistantTopLeaderCount = leaders.filter((l) =>
      l.positions.some((p) => isAssistantTopLeaderRole(p.position, unitType)),
    ).length;
    // V1.1: factual, bulleted line — includes Assistant Cubmaster count
    // explicitly (V1's summary omitted it entirely). Council-wide, 51 of
    // 69 Blackhawk Packs with a Cubmaster have 0 Assistant Cubmasters —
    // shown here as plain context, never as an Attention condition (see
    // computeAttentionSignals() below and docs/product-module-
    // architecture.md §17.13 for why). Assistant Den Leader is appended
    // only when nonzero, to keep the common (85%) zero-case compact.
    const summary = [
      topLeaderCount > 0 ? topLabel : `No ${topLabel} observed`,
      `${assistantTopLeaderCount} Assistant Cubmaster${assistantTopLeaderCount === 1 ? '' : 's'}`,
      `${denLeaderCount} Den Leader${denLeaderCount === 1 ? '' : 's'}`,
      ...(assistantDenLeaderCount > 0 ? [`${assistantDenLeaderCount} Assistant Den Leader${assistantDenLeaderCount === 1 ? '' : 's'}`] : []),
    ].join(' · ');
    return {
      availability: 'available',
      unitType,
      topLeaderLabel: topLabel,
      topLeaderCount,
      assistantTopLeaderLabel: 'Assistant Cubmaster',
      assistantTopLeaderCount,
      denLeaderCount,
      assistantDenLeaderCount,
      asOf,
      summary,
    };
  }

  if (unitType === 'troop') {
    const assistantTopLeaderCount = leaders.filter((l) =>
      l.positions.some((p) => isAssistantTopLeaderRole(p.position, unitType)),
    ).length;
    // V1.1: same bulleted style as the pack branch above, for consistency.
    const summary = [
      topLeaderCount > 0 ? topLabel : `No ${topLabel} observed`,
      `${assistantTopLeaderCount} Assistant Scoutmaster${assistantTopLeaderCount === 1 ? '' : 's'}`,
    ].join(' · ');
    return {
      availability: 'available',
      unitType,
      topLeaderLabel: topLabel,
      topLeaderCount,
      assistantTopLeaderLabel: 'Assistant Scoutmaster',
      assistantTopLeaderCount,
      denLeaderCount: null,
      assistantDenLeaderCount: null,
      asOf,
      summary,
    };
  }

  // Crew/Ship/Post/Club/Other — no confidently-mapped assistant-role title
  // for V1 (see the file header note), so only the top-leader observation
  // is shown. Deliberately not inventing a "den leader"-equivalent role.
  const summary = topLeaderCount > 0 ? `${topLabel} observed` : `No ${topLabel} observed`;
  return {
    availability: 'available',
    unitType,
    topLeaderLabel: topLabel,
    topLeaderCount,
    assistantTopLeaderLabel: null,
    assistantTopLeaderCount: null,
    denLeaderCount: null,
    assistantDenLeaderCount: null,
    asOf,
    summary,
  };
}

// ---- Role concentration --------------------------------------------------
// A UnitLeader doc's positions[] ALREADY represents every position one
// person holds within this one unit (see importTrainedLeaders()'s grouping
// key in lib/units.ts) — so "holds multiple positions in this unit" is
// simply positions.length > 1, no extra joining needed.

export interface ConcentrationLeader {
  memberId: string;
  fullName: string;
  positions: string[];
}

export interface RoleConcentration {
  availability: Availability;
  totalLeaders: number;
  multiRoleCount: number;
  multiRoleLeaders: ConcentrationLeader[];
  asOf: ISODateString | null;
}

export function computeRoleConcentration(
  leaders: UnitLeader[],
  trainedLeadersImportExists: boolean,
): RoleConcentration {
  if (!trainedLeadersImportExists) {
    return { availability: 'insufficient', totalLeaders: 0, multiRoleCount: 0, multiRoleLeaders: [], asOf: null };
  }
  const multiRoleLeaders = leaders
    .filter((l) => l.positions.length > 1)
    .map((l) => ({ memberId: l.memberId, fullName: l.fullName, positions: l.positions.map((p) => p.position) }));
  return {
    availability: 'available',
    totalLeaders: leaders.length,
    multiRoleCount: multiRoleLeaders.length,
    multiRoleLeaders,
    asOf: leaders[0]?.reportDate ?? null,
  };
}

// ---- Training + SYT readiness --------------------------------------------
// Position training and SYT are kept as two separate facts throughout —
// never collapsed into one "readiness score."

export interface ReadinessLeaderRef {
  memberId: string;
  fullName: string;
  untrainedPositions: string[];
}

export type SytLeaderStatus = 'current' | 'notCurrent' | 'noRecord';

export interface SytLeaderRef {
  memberId: string;
  fullName: string;
  status: SytLeaderStatus;
}

export interface LeadershipReadiness {
  availability: Availability;
  totalLeaders: number;
  trainedCount: number;
  trainedPercent: number | null;
  untrainedLeaders: ReadinessLeaderRef[];
  sytCurrentCount: number;
  sytNotCurrentCount: number;
  sytNoRecordCount: number;
  sytFlagged: SytLeaderRef[]; // notCurrent + noRecord, for drill-down
  asOf: ISODateString | null;
}

export function computeLeadershipReadiness(
  leaders: UnitLeader[],
  sytByMember: Map<string, Pick<UnitSyt, 'isCurrent'>>,
  trainedLeadersImportExists: boolean,
): LeadershipReadiness {
  if (!trainedLeadersImportExists || leaders.length === 0) {
    return {
      availability: 'insufficient',
      totalLeaders: 0,
      trainedCount: 0,
      trainedPercent: null,
      untrainedLeaders: [],
      sytCurrentCount: 0,
      sytNotCurrentCount: 0,
      sytNoRecordCount: 0,
      sytFlagged: [],
      asOf: null,
    };
  }
  const trainedCount = leaders.filter((l) => !l.anyUntrained).length;
  const untrainedLeaders = leaders
    .filter((l) => l.anyUntrained)
    .map((l) => ({
      memberId: l.memberId,
      fullName: l.fullName,
      untrainedPositions: l.positions.filter((p) => !p.trained).map((p) => p.position),
    }));

  let sytCurrentCount = 0;
  let sytNotCurrentCount = 0;
  let sytNoRecordCount = 0;
  const sytFlagged: SytLeaderRef[] = [];
  for (const l of leaders) {
    const rec = sytByMember.get(l.memberId);
    if (!rec) {
      sytNoRecordCount++;
      sytFlagged.push({ memberId: l.memberId, fullName: l.fullName, status: 'noRecord' });
    } else if (rec.isCurrent) {
      sytCurrentCount++;
    } else {
      sytNotCurrentCount++;
      sytFlagged.push({ memberId: l.memberId, fullName: l.fullName, status: 'notCurrent' });
    }
  }

  return {
    availability: 'available',
    totalLeaders: leaders.length,
    trainedCount,
    trainedPercent: Math.round((trainedCount / leaders.length) * 100),
    untrainedLeaders,
    sytCurrentCount,
    sytNotCurrentCount,
    sytNoRecordCount,
    sytFlagged,
    asOf: leaders[0]?.reportDate ?? null,
  };
}

// ---- Program-facing training readiness (V1.1) -----------------------------
// A NARROWER population than computeLeadershipReadiness() above — unique
// people holding a program-facing role (isProgramFacingRole()), not every
// registered leader. Exists ONLY to feed the narrower "no trained
// program-facing leader" Attention condition below; the broad, whole-
// roster Training fact shown in the UI still comes from
// computeLeadershipReadiness(), unchanged. See docs/product-module-
// architecture.md §17.13 for why V1's "any untrained leader anywhere"
// condition was replaced (94.3% council-wide, 7/7 in both Strong- and
// Weak-rated human-calibration groups — no differentiating value) rather
// than merely re-labeled.
//
// Requires BOTH a present top leader AND ≥1 identified program-facing
// leader before claiming `available` — a unit with no top leader on file
// (e.g. a thin/shadow linked-unit record) must read as Insufficient Data,
// never as a confident "no trained program-facing leader" claim, even if
// it happens to have an untrained assistant on file. This is the exact
// safeguard the V1.1 discovery's Troop 0072 (G) pathology finding called
// for.
export interface ProgramFacingReadiness {
  availability: Availability;
  /** false for unit types this concept isn't defined for at all
   *  (Crew/Ship/Post/Club/Other) — distinct from `availability:
   *  'insufficient'`, which can also happen for an applicable pack/troop
   *  with genuinely too little data. */
  applicable: boolean;
  totalCount: number | null;
  trainedCount: number | null;
  asOf: ISODateString | null;
}

export function computeProgramFacingReadiness(
  unit: Unit,
  leaders: UnitLeader[],
  trainedLeadersImportExists: boolean,
): ProgramFacingReadiness {
  const applicable = unit.type === 'pack' || unit.type === 'troop';
  if (!applicable || !trainedLeadersImportExists) {
    return { availability: 'insufficient', applicable, totalCount: null, trainedCount: null, asOf: null };
  }
  const topLeaderPresent = leaders.some((l) => l.positions.some((p) => isTopUnitLeaderRole(p.position, unit.type)));
  const facing = leaders.filter((l) => l.positions.some((p) => isProgramFacingRole(p.position, unit.type)));
  if (!topLeaderPresent || facing.length === 0) {
    return { availability: 'insufficient', applicable: true, totalCount: null, trainedCount: null, asOf: leaders[0]?.reportDate ?? null };
  }
  return {
    availability: 'available',
    applicable: true,
    totalCount: facing.length,
    trainedCount: facing.filter((l) => !l.anyUntrained).length,
    asOf: leaders[0]?.reportDate ?? null,
  };
}

// ---- Youth-to-leader context ----------------------------------------------
// Presented as context only — no built-in "good"/"bad" range. See the file
// header product principle.

export interface YouthLeaderContext {
  availability: Availability;
  youth: number | null;
  leaders: number | null;
  ratio: number | null;
  youthSource: 'recharter' | 'advancement' | null;
  asOf: ISODateString | null;
}

export function computeYouthLeaderContext(
  unit: Unit,
  leaders: UnitLeader[],
  trainedLeadersImportExists: boolean,
): YouthLeaderContext {
  let youth: number | null = null;
  let youthSource: 'recharter' | 'advancement' | null = null;
  let asOf: ISODateString | null = null;
  if (unit.recharter) {
    youth = unit.recharter.paidYouth;
    youthSource = 'recharter';
    asOf = unit.recharter.reportDate;
  } else {
    const program = unit.type === 'pack' ? 'cub' : 'scoutsBSA';
    const adv = unit.latestAdvancement?.[program];
    if (adv) {
      youth = adv.youth;
      youthSource = 'advancement';
      asOf = adv.reportDate;
    }
  }

  if (youth === null || !trainedLeadersImportExists) {
    return { availability: 'insufficient', youth, leaders: null, ratio: null, youthSource, asOf: null };
  }
  const leaderCount = leaders.length;
  if (leaderCount === 0) {
    return { availability: 'insufficient', youth, leaders: 0, ratio: null, youthSource, asOf };
  }
  return {
    availability: 'available',
    youth,
    leaders: leaderCount,
    ratio: Math.round((youth / leaderCount) * 10) / 10,
    youthSource,
    asOf,
  };
}

// ---- Leadership history (from unitLeaderSnapshots) ------------------------
// Degrades gracefully: 0 snapshots -> 'none' (current-state sections above
// still work independently), 1 snapshot -> 'single' (no trend claimed), 2+
// distinct dates -> 'comparable' (a real delta between the two most recent
// dates). Never fabricates a trend from fewer than 2 comparable dates.

export interface SnapshotPersonRef {
  memberId: string;
  fullName: string;
  positions: string[];
}

export interface SnapshotPositionChange {
  memberId: string;
  fullName: string;
  added: string[];
  removed: string[];
}

export interface SnapshotDelta {
  priorDate: ISODateString;
  currentDate: ISODateString;
  leadersAdded: SnapshotPersonRef[];
  leadersDeparted: SnapshotPersonRef[];
  positionChanges: SnapshotPositionChange[];
  denLeaderDelta: number | null; // pack only
  assistantTopLeaderDelta: number | null; // pack + troop only
  depthDelta: number;
  concentrationDelta: number; // change in count of leaders holding >1 position
}

export interface TenureObservation {
  memberId: string;
  fullName: string;
  position: string;
  /** The earliest snapshot date this (member, position) pair was observed
   *  at this unit — NOT necessarily when they actually started; see
   *  describeTenure() below for the required phrasing. */
  observedSinceAtLeast: ISODateString;
}

export interface LeadershipHistory {
  availability: 'none' | 'single' | 'comparable';
  snapshotDates: ISODateString[]; // distinct dates this unit has history for, oldest first
  latestDate: ISODateString | null;
  delta: SnapshotDelta | null;
  tenure: TenureObservation[]; // one row per (member, position) currently held, if any history exists
}

/** Careful, deliberately hedged phrasing per the product spec — a person's
 *  earliest appearance in Compass history is not proof of when they
 *  actually started. This wording must survive even after Compass
 *  accumulates years of history. */
export function describeTenure(t: TenureObservation): string {
  return `Observed in role since at least ${t.observedSinceAtLeast}`;
}

function groupByDate(snapshots: UnitLeaderSnapshot[]): Map<ISODateString, UnitLeaderSnapshot[]> {
  const map = new Map<ISODateString, UnitLeaderSnapshot[]>();
  for (const s of snapshots) {
    const arr = map.get(s.snapshotDate) ?? [];
    arr.push(s);
    map.set(s.snapshotDate, arr);
  }
  return map;
}

function computeDelta(
  prior: { date: ISODateString; rows: UnitLeaderSnapshot[] },
  current: { date: ISODateString; rows: UnitLeaderSnapshot[] },
  unitType: UnitType,
): SnapshotDelta {
  const priorByMember = new Map(prior.rows.map((r) => [r.memberId, r]));
  const currentByMember = new Map(current.rows.map((r) => [r.memberId, r]));

  const leadersAdded: SnapshotPersonRef[] = [];
  const leadersDeparted: SnapshotPersonRef[] = [];
  const positionChanges: SnapshotPositionChange[] = [];

  for (const [memberId, row] of currentByMember) {
    if (!priorByMember.has(memberId)) {
      leadersAdded.push({ memberId, fullName: `${row.firstName} ${row.lastName}`.trim(), positions: row.positions });
    }
  }
  for (const [memberId, row] of priorByMember) {
    if (!currentByMember.has(memberId)) {
      leadersDeparted.push({ memberId, fullName: `${row.firstName} ${row.lastName}`.trim(), positions: row.positions });
    }
  }
  for (const [memberId, currentRow] of currentByMember) {
    const priorRow = priorByMember.get(memberId);
    if (!priorRow) continue;
    const priorSet = new Set(priorRow.positions);
    const currentSet = new Set(currentRow.positions);
    const added = currentRow.positions.filter((p) => !priorSet.has(p));
    const removed = priorRow.positions.filter((p) => !currentSet.has(p));
    if (added.length || removed.length) {
      positionChanges.push({ memberId, fullName: `${currentRow.firstName} ${currentRow.lastName}`.trim(), added, removed });
    }
  }

  const countRole = (rows: UnitLeaderSnapshot[], test: (position: string) => boolean) =>
    rows.filter((r) => r.positions.some(test)).length;
  const countConcentration = (rows: UnitLeaderSnapshot[]) => rows.filter((r) => r.positions.length > 1).length;

  const denLeaderDelta =
    unitType === 'pack'
      ? countRole(current.rows, isDenLeaderRole) - countRole(prior.rows, isDenLeaderRole)
      : null;
  const assistantTopLeaderDelta =
    unitType === 'pack' || unitType === 'troop'
      ? countRole(current.rows, (p) => isAssistantTopLeaderRole(p, unitType)) -
        countRole(prior.rows, (p) => isAssistantTopLeaderRole(p, unitType))
      : null;

  return {
    priorDate: prior.date,
    currentDate: current.date,
    leadersAdded,
    leadersDeparted,
    positionChanges,
    denLeaderDelta,
    assistantTopLeaderDelta,
    depthDelta: current.rows.length - prior.rows.length,
    concentrationDelta: countConcentration(current.rows) - countConcentration(prior.rows),
  };
}

/** `snapshots` should be ALL of this unit's recorded history (see
 *  useUnitLeaderSnapshots(unitId) with no date filter), any order — this
 *  function sorts internally. */
export function computeLeadershipHistory(snapshots: UnitLeaderSnapshot[], unitType: UnitType): LeadershipHistory {
  const byDate = groupByDate(snapshots);
  const dates = [...byDate.keys()].sort();

  if (dates.length === 0) {
    return { availability: 'none', snapshotDates: [], latestDate: null, delta: null, tenure: [] };
  }

  // Apparent tenure: for every (member, position) pair CURRENTLY observed
  // (at the latest snapshot date), the earliest date across ALL history
  // that pair appears — using careful "observed since at least" language,
  // never asserted as the real start date.
  const latestDate = dates[dates.length - 1];
  const latestRows = byDate.get(latestDate)!;
  const tenure: TenureObservation[] = [];
  for (const row of latestRows) {
    for (const position of row.positions) {
      let earliest: ISODateString = latestDate;
      for (const d of dates) {
        const rows = byDate.get(d)!;
        const match = rows.find((r) => r.memberId === row.memberId && r.positions.includes(position));
        if (match) {
          earliest = d;
          break;
        }
      }
      tenure.push({
        memberId: row.memberId,
        fullName: `${row.firstName} ${row.lastName}`.trim(),
        position,
        observedSinceAtLeast: earliest,
      });
    }
  }

  if (dates.length === 1) {
    return { availability: 'single', snapshotDates: dates, latestDate, delta: null, tenure };
  }

  const priorDate = dates[dates.length - 2];
  const delta = computeDelta(
    { date: priorDate, rows: byDate.get(priorDate)! },
    { date: latestDate, rows: latestRows },
    unitType,
  );
  return { availability: 'comparable', snapshotDates: dates, latestDate, delta, tenure };
}

// ---- Attention signals ---------------------------------------------------
// Every signal must be reproducible from the facts computed above — no
// signal here invents a fact of its own. See ATTENTION SIGNAL DEFINITIONS
// in docs/product-module-architecture.md for the authoritative prose
// version of each definition below.

export type AttentionSignalId =
  | 'missingKeyLeadership'
  | 'topLeaderSupportAttention'
  | 'concentratedLeadership'
  | 'leadershipLoss'
  | 'programLeaderLoss'
  | 'trainingAttention'
  | 'sytAttention';

export interface AttentionSignal {
  id: AttentionSignalId;
  label: string;
  detail: string;
}

export const ATTENTION_SIGNAL_LABELS: Record<AttentionSignalId, string> = {
  missingKeyLeadership: 'Missing Key Leadership',
  topLeaderSupportAttention: 'Top-Leader Support Attention',
  concentratedLeadership: 'Concentrated Roles',
  leadershipLoss: 'Leadership Loss Observed',
  programLeaderLoss: 'Leadership Loss Observed',
  trainingAttention: 'Training Attention',
  sytAttention: 'SYT Attention',
};

/** Concentration is flagged when a MAJORITY (more than half) of a unit's
 *  registered leaders hold multiple positions — a plain, descriptively-true
 *  statement ("most of this unit's leaders...") rather than a tuned
 *  percentage. This is the one place V1 makes a discretionary proportion
 *  judgment at all; every other signal below fires on a bare nonzero fact
 *  (any missing seat, any departure, any untrained leader), never a tuned
 *  threshold. Revisit this specific choice once real Compass observations
 *  can be compared against units district leadership already knows well —
 *  see the product spec's own §12 closing note. */
function isConcentrationMajority(concentration: RoleConcentration): boolean {
  return concentration.availability === 'available' && concentration.multiRoleCount * 2 > concentration.totalLeaders;
}

export function computeAttentionSignals(input: {
  unit: Unit;
  keyLeadership: KeyLeadershipCoverage;
  programLeadership: ProgramLeadershipCoverage;
  concentration: RoleConcentration;
  readiness: LeadershipReadiness;
  history: LeadershipHistory;
  programFacing: ProgramFacingReadiness;
}): AttentionSignal[] {
  const { keyLeadership, programLeadership, concentration, readiness, history, programFacing } = input;
  const signals: AttentionSignal[] = [];

  if (keyLeadership.availability === 'available') {
    for (const seat of keyLeadership.seats) {
      if (!seat.observed) {
        signals.push({
          id: 'missingKeyLeadership',
          label: ATTENTION_SIGNAL_LABELS.missingKeyLeadership,
          detail: `${seat.seatLabel} not observed in current Key 3 data.`,
        });
      }
    }
  }

  // V1.1: Troop-only. The V1 "Thin Program Leadership" condition combined
  // two different concepts under one label — for Troops, 0 Assistant
  // Scoutmasters really is Compass's only available signal for backup
  // behind the top leader (no separate program-delivery role exists in
  // this data model for Troops). For Packs, the equivalent condition (0
  // Den Leaders) essentially never fires on real data (0 of 69 Blackhawk
  // Packs with a Cubmaster) while the genuinely common, separately
  // meaningful fact — 0 Assistant Cubmasters, true for 51 of 69 — is left
  // as factual Program Leadership context only (see computeProgramLeadership()
  // above), not an Attention condition, per explicit product decision: it
  // is too common (73.9% of Packs) to be selective, and Compass cannot
  // infer succession planning from its absence. See docs/product-module-
  // architecture.md §17.13.
  if (
    programLeadership.availability === 'available' &&
    programLeadership.unitType === 'troop' &&
    programLeadership.topLeaderCount > 0 &&
    programLeadership.assistantTopLeaderCount === 0
  ) {
    signals.push({
      id: 'topLeaderSupportAttention',
      label: ATTENTION_SIGNAL_LABELS.topLeaderSupportAttention,
      detail: 'No Assistant Scoutmasters registered.',
    });
  }

  if (isConcentrationMajority(concentration)) {
    signals.push({
      id: 'concentratedLeadership',
      label: ATTENTION_SIGNAL_LABELS.concentratedLeadership,
      detail: `${concentration.multiRoleCount} of ${concentration.totalLeaders} registered leaders hold multiple positions.`,
    });
  }

  if (history.availability === 'comparable' && history.delta) {
    const { delta } = history;
    if (delta.leadersDeparted.length > 0) {
      signals.push({
        id: 'leadershipLoss',
        label: ATTENTION_SIGNAL_LABELS.leadershipLoss,
        detail: `${delta.leadersDeparted.length} registered leader${delta.leadersDeparted.length === 1 ? '' : 's'} no longer observed since the ${delta.priorDate} snapshot.`,
      });
    }
    if (delta.denLeaderDelta !== null && delta.denLeaderDelta < 0) {
      signals.push({
        id: 'programLeaderLoss',
        label: ATTENTION_SIGNAL_LABELS.programLeaderLoss,
        detail: `Pack has ${Math.abs(delta.denLeaderDelta)} fewer Den Leader${Math.abs(delta.denLeaderDelta) === 1 ? '' : 's'} than the ${delta.priorDate} snapshot.`,
      });
    }
    if (delta.assistantTopLeaderDelta !== null && delta.assistantTopLeaderDelta < 0) {
      const roleWord = input.unit.type === 'troop' ? 'Assistant Scoutmaster' : 'Assistant Cubmaster';
      signals.push({
        id: 'programLeaderLoss',
        label: ATTENTION_SIGNAL_LABELS.programLeaderLoss,
        detail: `Unit has ${Math.abs(delta.assistantTopLeaderDelta)} fewer ${roleWord}${Math.abs(delta.assistantTopLeaderDelta) === 1 ? '' : 's'} than the ${delta.priorDate} snapshot.`,
      });
    }
  }

  // V1.1: narrowed from "any registered leader anywhere is untrained"
  // (which fired on 165/180 council-wide, and 7/7 in BOTH the human-
  // rated Strong and Weak calibration groups — no differentiating value)
  // to "zero of this unit's program-facing leaders are trained." Requires
  // programFacing.availability === 'available', which itself requires a
  // present top leader AND ≥1 identified program-facing person — a unit
  // that can't reliably support this conclusion gets no signal at all,
  // never a guessed negative one. See computeProgramFacingReadiness()
  // above and docs/product-module-architecture.md §17.13. The broad,
  // whole-roster trained-count/percent fact is unaffected — see
  // readiness.trainedCount/trainedPercent above, still shown in the UI.
  if (programFacing.availability === 'available' && programFacing.trainedCount === 0) {
    signals.push({
      id: 'trainingAttention',
      label: ATTENTION_SIGNAL_LABELS.trainingAttention,
      detail: 'No trained program-facing leader is currently identified.',
    });
  }

  if (readiness.availability === 'available' && readiness.sytNotCurrentCount > 0) {
    signals.push({
      id: 'sytAttention',
      label: ATTENTION_SIGNAL_LABELS.sytAttention,
      detail: `${readiness.sytNotCurrentCount} of ${readiness.totalLeaders} leaders are not current on Safeguarding Youth Training.`,
    });
  }

  return signals;
}

// ---- Commissioner enrichment reservation (V1: always null) ---------------
// Deliberately empty in V1 — see the Commissioner Tools boundary note at
// the top of this file. Kept as a typed, always-null field (not simply
// absent) so a future Commissioner enrichment slots in as a field fill,
// not a redesign.

export interface CommissionerContext {
  assignedCommissionerName: string | null;
  lastEngagementDate: ISODateString | null;
  notes: string | null;
}

// ---- Top-level aggregate --------------------------------------------------

export interface UnitLeadershipSustainability {
  unitId: string;
  unitType: UnitType;
  depth: LeadershipDepth;
  keyLeadership: KeyLeadershipCoverage;
  programLeadership: ProgramLeadershipCoverage;
  concentration: RoleConcentration;
  readiness: LeadershipReadiness;
  /** V1.1 — see computeProgramFacingReadiness() above. Feeds the narrower
   *  Training Attention condition; not itself shown as a separate UI
   *  block (the broad `readiness` fact above already covers factual
   *  display — see docs/product-module-architecture.md §17.13). */
  programFacing: ProgramFacingReadiness;
  youthContext: YouthLeaderContext;
  history: LeadershipHistory;
  attentionSignals: AttentionSignal[];
  /** Always null in V1 — see CommissionerContext above. */
  commissioner: CommissionerContext;
}

export interface ComputeLeadershipSustainabilityInput {
  unit: Unit;
  /** This unit's own unitLeaders docs ONLY — scope==='unit' &&
   *  unitId===unit.id, already resolved. Never pass unresolved-unitId
   *  records here (see leadersByUnit in UnitHealth.tsx, which already
   *  excludes them by construction). */
  unitLeaders: UnitLeader[];
  sytByMember: Map<string, Pick<UnitSyt, 'isCurrent'>>;
  trainedLeadersImportExists: boolean;
  /** This unit's full unitLeaderSnapshots history, any order. */
  snapshots: UnitLeaderSnapshot[];
}

export function computeUnitLeadershipSustainability(
  input: ComputeLeadershipSustainabilityInput,
): UnitLeadershipSustainability {
  const { unit, unitLeaders, sytByMember, trainedLeadersImportExists, snapshots } = input;
  const depth = computeLeadershipDepth(unitLeaders, trainedLeadersImportExists);
  const keyLeadership = computeKeyLeadership(unit);
  const programLeadership = computeProgramLeadership(unit, unitLeaders, trainedLeadersImportExists);
  const concentration = computeRoleConcentration(unitLeaders, trainedLeadersImportExists);
  const readiness = computeLeadershipReadiness(unitLeaders, sytByMember, trainedLeadersImportExists);
  const programFacing = computeProgramFacingReadiness(unit, unitLeaders, trainedLeadersImportExists);
  const youthContext = computeYouthLeaderContext(unit, unitLeaders, trainedLeadersImportExists);
  const history = computeLeadershipHistory(snapshots, unit.type);
  const attentionSignals = computeAttentionSignals({
    unit,
    keyLeadership,
    programLeadership,
    concentration,
    readiness,
    history,
    programFacing,
  });

  return {
    unitId: unit.id,
    unitType: unit.type,
    depth,
    keyLeadership,
    programLeadership,
    concentration,
    readiness,
    programFacing,
    youthContext,
    history,
    attentionSignals,
    commissioner: { assignedCommissionerName: null, lastEngagementDate: null, notes: null },
  };
}

// ---- Council/district attention categories --------------------------------
// The district/council "attention view" (UnitHealth.tsx) buckets units by
// these categories — deliberately NOT a ranked leaderboard; a unit can, and
// often will, appear in more than one bucket.

export type AttentionCategory =
  | 'missingKeyLeadership'
  | 'topLeaderSupportAttention'
  | 'leadershipLossObserved'
  | 'trainingAttention'
  | 'sytAttention'
  | 'concentratedRoles'
  | 'insufficientData';

export const ATTENTION_CATEGORY_LABELS: Record<AttentionCategory, string> = {
  missingKeyLeadership: 'Missing Key Leadership',
  topLeaderSupportAttention: 'Top-Leader Support Attention',
  leadershipLossObserved: 'Leadership Loss Observed',
  trainingAttention: 'Training Attention',
  sytAttention: 'SYT Attention',
  concentratedRoles: 'Concentrated Roles',
  insufficientData: 'Insufficient Data',
};

export function attentionCategoriesFor(result: UnitLeadershipSustainability): AttentionCategory[] {
  const categories = new Set<AttentionCategory>();
  for (const s of result.attentionSignals) {
    if (s.id === 'missingKeyLeadership') categories.add('missingKeyLeadership');
    if (s.id === 'topLeaderSupportAttention') categories.add('topLeaderSupportAttention');
    if (s.id === 'leadershipLoss' || s.id === 'programLeaderLoss') categories.add('leadershipLossObserved');
    if (s.id === 'trainingAttention') categories.add('trainingAttention');
    if (s.id === 'sytAttention') categories.add('sytAttention');
    if (s.id === 'concentratedLeadership') categories.add('concentratedRoles');
  }
  if (result.depth.availability === 'insufficient' || result.keyLeadership.availability === 'insufficient') {
    categories.add('insufficientData');
  }
  return [...categories];
}
